import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ChevronDown, ChevronUp, Wheat, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

// Import extracted data and components
import { sortimentData, breadCategoryIds } from '../data/products';
import { CategorySection } from '../components/sortiment';

// Anchor Navigation Component
const AnchorNav = () => {
  const categories = sortimentData.categories;
  
  return (
    <nav className="bg-white/95 backdrop-blur-sm sticky top-[52px] sm:top-[68px] z-40 border-b border-[#e5e2df] shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex flex-wrap justify-center gap-2">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={`#${cat.id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#f7f5f2] hover:bg-[#fad24b]/40 text-[#1e1919] text-xs font-medium rounded-full transition-colors border border-transparent hover:border-[#fad24b]"
            >
              <span>{cat.icon}</span>
              <span>{cat.title}</span>
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
};

// Lagerung Info Component
const LagerungSection = () => (
  <section className="mb-12 sm:mb-16 bg-[#61525a]/10 rounded-2xl p-6 sm:p-10">
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl sm:text-3xl font-bold text-[#1e1919] mb-6 flex items-center gap-3">
        <span className="text-3xl">📦</span>
        Richtige Lagerung
      </h2>
      
      <div className="space-y-4 text-[#736c64] text-sm sm:text-base leading-relaxed">
        <p>
          Bei richtiger Lagerung sind dunkle Brotsorten, vor allem Brote aus Roggenmehl, zirka fünf Tage haltbar. 
          Brot muss atmen können. Wir empfehlen deshalb, es im guten alten Brotkasten aus Holz oder einem Leinensack 
          zu lagern. So bleibt das Brot frisch und das Aroma erhalten.
        </p>
        
        <p>
          Denken Sie speziell im Sommer bei heißer und feuchter Witterung daran, diesen Behälter öfters gründlich 
          zu reinigen (z.B. mit Essigwasser), um Schimmelbildung zu verhindern. Keinesfalls sollten Sie Brot im 
          Kühlschrank lagern, dort verliert es sehr rasch an Geschmack. Als Vorrat kann man Brot auch einfrieren – 
          gut verpackt hält es einige Wochen.
        </p>

        <div className="bg-white/60 rounded-xl p-4 sm:p-6 mt-6">
          <h3 className="font-semibold text-[#1e1919] mb-3">Wichtige Hinweise</h3>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-[#fad24b] mt-1">•</span>
              Aus produktionstechnischen Gründen und rohstoffbedingt kann es zu geringfügigen Schwankungen bei den angegebenen Zutaten kommen.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#fad24b] mt-1">•</span>
              Auf Grund der handwerklichen Verarbeitung können sich auch trotz größter Sorgfalt Spuren anderer Zutaten als angegeben in den Backwaren befinden.
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// Trust Factor Component for Brioche
const TrustFactorSection = () => (
  <section className="mb-12 sm:mb-16 bg-gradient-to-br from-[#fad24b]/20 to-[#fad24b]/5 rounded-2xl p-6 sm:p-10 border border-[#fad24b]/30">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <span className="inline-block bg-[#fad24b] text-[#1e1919] text-xs font-bold px-3 py-1 rounded-full mb-3">
          QUALITÄTSVERSPRECHEN
        </span>
        <h2 className="text-xl sm:text-2xl font-bold text-[#1e1919]">
          Für Original-Bäcker Butter-Brioche werden folgende ausgesuchte Rohstoffe verwendet:
        </h2>
      </div>
      
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { title: "Österreichisches Qualitätsweizenmehl", desc: "Grundvoraussetzung für höchsten Genuss" },
          { title: "Vollmilch", desc: "Für kurzen Biss, guten Geschmack und lange Frischhaltung" },
          { title: "Frische Eier", desc: "Für ansprechendes Volumen und guten Geschmack" },
          { title: "Reine Butter", desc: "Für ausgezeichneten Geschmack und lange Frischhaltung" },
          { title: "Rohzucker", desc: "Für besonders aromatischen Geschmack, enthält viele Mineralstoffe und Spurenelemente" },
          { title: "Naturkristallsalz", desc: "Unraffiniert mit allen 84 Elementen und Spurenelementen" },
          { title: "Hefe", desc: "Zur Lockerung der Krume" },
          { title: "Vanille", desc: "Trifft den Geschmack fast aller Konsumenten" },
          { title: "Kartoffelpüree", desc: "Hält das Gebäck auch noch am nächsten Tag frisch" }
        ].map((item, idx) => (
          <div key={idx} className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-bold text-[#1e1919] text-sm mb-1">{item.title}</h3>
            <p className="text-[#736c64] text-xs">{item.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-[#61525a] text-white rounded-xl p-4 sm:p-5">
        <p className="text-sm flex items-start gap-2">
          <span className="text-[#fad24b] font-bold">💡 Tipp:</span>
          <span>Bewahren Sie Brioche immer in einer Plastikdose, foliert oder in einem Nylonsackerl auf. Wenn Sie Brioche auftauen, bitte nicht in den Ofen geben sondern nur bei Raumtemperatur aufgehen lassen.</span>
        </p>
      </div>
    </div>
  </section>
);

// Quality Info Panel Component
const QualityInfoPanel = () => (
  <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-2xl p-6 max-w-3xl mx-auto text-left">
    <div className="grid sm:grid-cols-2 gap-6">
      {/* Mehl Info */}
      <div>
        <h3 className="font-bold text-[#fad24b] mb-2 flex items-center gap-2">
          <Wheat className="w-5 h-5" />
          {sortimentData.mehlInfo.name}
        </h3>
        <p className="text-white/70 text-sm mb-3">
          {sortimentData.mehlInfo.verfahren}
        </p>
        <ul className="space-y-1">
          {sortimentData.mehlInfo.ernaehrungsvorteile.map((vorteil, idx) => (
            <li key={idx} className="text-white/90 text-sm flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#fad24b] rounded-full"></span>
              {vorteil}
            </li>
          ))}
        </ul>
      </div>

      {/* Salz Info */}
      <div>
        <h3 className="font-bold text-[#fad24b] mb-2 flex items-center gap-2">
          <Info className="w-5 h-5" />
          {sortimentData.salzInfo.type}
        </h3>
        <ul className="space-y-1">
          {sortimentData.salzInfo.eigenschaften.map((eigenschaft, idx) => (
            <li key={idx} className="text-white/90 text-sm flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#fad24b] rounded-full"></span>
              {eigenschaft}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const SortimentPage = () => {
  const [showMehlInfo, setShowMehlInfo] = useState(false);

  // Get category indices for rendering logic
  const plunderIndex = sortimentData.categories.findIndex(c => c.id === 'feines-plundergebaeck');

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      <Helmet>
        <title>Sortiment – Brot, Gebäck & Mehlspeisen | Cafe Konditorei Rieppel Wien</title>
        <meta name="description" content="Entdecken Sie über 60 handgefertigte Backwaren: Mischbrot, Vollkornbrot, Semmeln, Plundergebäck, Torten & mehr. Täglich frisch aus unserer Naturbackstube in 1150 Wien." />
        <link rel="canonical" href="https://rieppel-baeckerei.at/sortiment" />
        <meta property="og:title" content="Sortiment – Brot, Gebäck & Mehlspeisen | Cafe Konditorei Rieppel" />
        <meta property="og:description" content="Über 60 handgefertigte Backwaren aus unserer Naturbackstube. Täglich frisch – seit 1938 Wiener Backtradition." />
        <meta property="og:url" content="https://rieppel-baeckerei.at/sortiment" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_7e52a33b-4c46-4bf0-be21-c264c50abfa9/artifacts/8cty36r8_image.png" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Sortiment – Brot, Gebäck & Mehlspeisen | Cafe Konditorei Rieppel" />
        <meta property="twitter:description" content="Über 60 handgefertigte Backwaren aus unserer Naturbackstube in Wien. Täglich frisch – seit 1938." />
        <meta property="twitter:image" content="https://customer-assets.emergentagent.com/job_7e52a33b-4c46-4bf0-be21-c264c50abfa9/artifacts/8cty36r8_image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://rieppel-baeckerei.at/" },
            { "@type": "ListItem", "position": 2, "name": "Sortiment" }
          ]
        })}</script>
      </Helmet>
      {/* Header */}
      <header className="bg-[#1e1919] text-white py-4 sm:py-6 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Zurück zur Startseite</span>
          </Link>
          <h1 className="font-bold text-lg sm:text-xl">Unser Sortiment</h1>
          <div className="w-20 sm:w-32"></div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-[#61525a] text-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <span className="inline-block text-[#fad24b] font-medium text-xs sm:text-sm tracking-wider uppercase mb-3">
            Brot, Gebäck & Mehlspeisen
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
            Unser Sortiment
          </h1>
          <p className="text-white/80 text-base sm:text-lg max-w-2xl mx-auto mb-6">
            Entdecken Sie unsere handgefertigten Backwaren – täglich frisch gebacken 
            nach traditionellen Rezepten mit hochwertigen Zutaten.
          </p>

          {/* Quality Info Button */}
          <button
            onClick={() => setShowMehlInfo(!showMehlInfo)}
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded-full px-5 py-2.5 transition-colors"
            data-testid="quality-info-toggle"
          >
            <Wheat className="w-5 h-5 text-[#fad24b]" />
            <span className="text-sm sm:text-base">Unsere Qualitätsversprechen</span>
            {showMehlInfo ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {/* Quality Info Panel */}
          {showMehlInfo && <QualityInfoPanel />}
        </div>
      </section>

      {/* Anchor Navigation */}
      <AnchorNav />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Bread Categories */}
        {sortimentData.categories
          .filter(cat => breadCategoryIds.includes(cat.id))
          .map((category, catIndex) => (
            <CategorySection key={category.id} category={category} isFirstCategory={catIndex === 0} />
          ))}

        {/* Lagerung Section - after breads */}
        <LagerungSection />

        {/* Other Categories up to Feines Plundergebäck */}
        {sortimentData.categories
          .filter(cat => !breadCategoryIds.includes(cat.id) && cat.id !== 'feines-plundergebaeck')
          .slice(0, plunderIndex - breadCategoryIds.length)
          .map((category) => (
            <CategorySection key={category.id} category={category} />
          ))}

        {/* Feines Plundergebäck (contains Brioche) */}
        {sortimentData.categories
          .filter(cat => cat.id === 'feines-plundergebaeck')
          .map((category) => (
            <CategorySection key={category.id} category={category} />
          ))}

        {/* Trust Factor - right after Brioche/Plundergebäck */}
        <TrustFactorSection />

        {/* Remaining Categories */}
        {sortimentData.categories
          .filter((cat, idx) => {
            return !breadCategoryIds.includes(cat.id) && cat.id !== 'feines-plundergebaeck' && idx > plunderIndex;
          })
          .map((category) => (
            <CategorySection key={category.id} category={category} />
          ))}

        {/* Closing Message */}
        <div className="text-center pt-12 mt-12 border-t border-[#61525a]/20">
          <p className="text-[#61525a] italic">
            Wir hoffen, mit unseren Produkten Ihren Geschmack getroffen zu haben und Sie als zufriedenen Kunden bei uns begrüßen zu dürfen.
          </p>
          <p className="font-semibold text-[#1e1919] mt-2">
            Ihre Handwerks-Bäckerei Rieppel
          </p>
        </div>
      </main>

      {/* Call to Action */}
      <section className="bg-[#1e1919] text-white py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Fragen zu unserem Sortiment?
          </h2>
          <p className="text-white/70 mb-6">
            Besuchen Sie uns in der Bäckerei oder rufen Sie uns an – wir beraten Sie gerne!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:01 9825285"
              className="inline-flex items-center justify-center gap-2 bg-[#61525a] hover:bg-[#4a3f45] text-white px-6 py-3 rounded-full font-medium transition-colors"
              data-testid="call-button"
            >
              Jetzt anrufen
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-6 py-3 rounded-full font-medium transition-colors"
              data-testid="home-link"
            >
              Zur Startseite
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SortimentPage;
